import React, { useState } from 'react';
import { Box, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import { AuthPermissions } from 'skipify-types';

import { Button } from '../../components/core';
import { SearchTextField } from '../../pages/Paylinks/components';
import { useMerchant } from '../../contexts/MerchantContext';
import { useCustomerSubmit, useCustomersUpload } from '../../pages/Paylinks/hooks';
import CustomerModal from '../../pages/Paylinks/components/CustomerModal';
import UploadCustomersModal from '../../pages/Paylinks/components/UploadCustomersModal';

interface Props {
  merchantId: string;
  defaultSearchQuery?: string;
  onSearch: (query: string) => void;
  isFilteringByCardOnFile?: boolean;
  onFilterByCardOnFile?: (isFilteringByCardOnFile: boolean) => void;
}

export const CustomersTableHeader = ({
  merchantId,
  defaultSearchQuery,
  onSearch,
  isFilteringByCardOnFile,
  onFilterByCardOnFile,
}: Props) => {
  const { canAccess, hasCardOnFileAccess } = useMerchant();
  const hasCardAccess = hasCardOnFileAccess();

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const { onCustomerSubmit, serverValidationError } = useCustomerSubmit({ merchantId });
  const { onCustomerUpload, uploadValidationError } = useCustomersUpload({ merchantId });

  return (
    <>
      <Stack gap={2}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            flexDirection: { xs: 'column', md: 'row' },
            width: '100%',
            gap: 2,
            '& button': { width: { xs: '100%', md: '200px' } },
          }}
        >
          <Typography
            component="h2"
            fontWeight="semibold"
            sx={{ fontSize: { xs: '16px', md: '20px' } }}
          >
            Customers
          </Typography>
          <Stack direction="row" spacing={2} sx={{ width: { xs: '100%', md: 'auto' } }}>
            <Button
              theme="secondary"
              text="Upload Customers"
              disabled={!canAccess(AuthPermissions.CREATE_PAYLINK_CUSTOMERS)}
              onClick={() => setIsUploadModalOpen(true)}
            />
            <Button
              theme="primary"
              text="Add Customer"
              disabled={!canAccess(AuthPermissions.CREATE_PAYLINK_CUSTOMERS)}
              onClick={() => setIsAddModalOpen(true)}
            />
          </Stack>
        </Box>
        <Box sx={{ display: 'flex', gap: 3, width: '100%' }}>
          <SearchTextField
            label="Search by customer name or customer email"
            onSearch={onSearch}
            defaultValue={defaultSearchQuery}
          />
        </Box>
        {onFilterByCardOnFile && hasCardAccess && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              justifyContent: 'flex-end',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  color="link"
                  onChange={() => onFilterByCardOnFile(!isFilteringByCardOnFile)}
                  checked={!!isFilteringByCardOnFile}
                  inputProps={{ 'aria-labelledby': 'card-on-file-filter' }}
                />
              }
              label="Only show customers with a card on file"
              id="card-on-file-filter"
            />
          </Box>
        )}
      </Stack>
      <CustomerModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={onCustomerSubmit}
        serverValidationError={serverValidationError}
        type="Add"
      />
      <UploadCustomersModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onSubmit={onCustomerUpload}
        serverValidationError={uploadValidationError}
      />
    </>
  );
};
